import { useState } from 'react';
import { Switch, Badge, Button, Dialog, useToast } from '@dreamtree-org/twreact-ui';
import { SectionCard, QueryState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { errorText } from '@/lib/format';
import Can from '@rbac/Can';
import { inFlightOf, SWITCH_OFF_CONSEQUENCE } from '../../lib/features';

/**
 * Store features (D101): the parts of MerchantPro this store offers, switched on or
 * off by someone holding store:features (the owner; the server checks it and audits
 * every change). Off stops new bookings and hides the staff screens; shoppers still
 * finish the repairs they have (tracker, estimate, return, invoice). Warranty is always
 * on. Switching repairs off with open tickets or unpaid repair invoices is
 * refused with the counts (409 FEATURE_IN_USE): the owner sees them and confirms.
 * Everyone with store:view sees the state.
 */
export default function FeaturesTab() {
  const { toast } = useToast();
  const q = useQuery('storeFeatures', {}, { select: (r) => (r && r.data) || [] });
  const save = useMutation('setStoreFeatures');
  const [pending, setPending] = useState(null); // { feature, counts } waiting for confirmation

  const run = async (f, on, confirm = false) => {
    try {
      await save.run({ features: { [f.key]: on }, ...(confirm ? { confirm: true } : {}) });
      setPending(null);
      toast.success(on ? `${f.label} switched on` : `${f.label} switched off`, on ? 'Shoppers and staff can use it again.' : 'New bookings and staff screens are closed; customers can still finish their repairs.');
      q.reload();
    } catch (ex) {
      const counts = inFlightOf(ex);
      if (counts && !confirm) setPending({ feature: f, counts });
      else toast.error('Could not change the feature', errorText(ex));
    }
  };

  return (
    <QueryState query={q}>
      {(features) => (
        <SectionCard title="Features">
          <p className="text-sm opacity-70">Choose what your store offers besides selling. Warranty claims stay on for every store.</p>
          <ul className="divide-y divide-[var(--app-border)] rounded-lg border border-[var(--app-border)]">
            {features.map((f) => (
              <li key={f.key} className="flex min-h-[44px] items-center justify-between gap-3 px-3 py-2">
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{f.label}</span>
                  <span className="block text-xs opacity-70">{f.description}</span>
                </span>
                <Can module="store" action="features" fallback={<Badge variant={f.enabled ? 'success' : 'secondary'} size="sm">{f.enabled ? 'On' : 'Off'}</Badge>}>
                  <Switch checked={!!f.enabled} disabled={save.loading} onChange={(on) => run(f, on)} ariaLabel={`${f.label}: ${f.enabled ? 'on' : 'off'}`} />
                </Can>
              </li>
            ))}
          </ul>
          <Dialog
            isOpen={!!pending}
            onClose={() => setPending(null)}
            title={pending ? `Switch ${pending.feature.label.toLowerCase()} off?` : ''}
            footer={
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => setPending(null)}>Keep it on</Button>
                <Button variant="destructive" loading={save.loading} onClick={() => run(pending.feature, false, true)}>Switch off anyway</Button>
              </div>
            }
          >
            {pending ? (
              <div className="space-y-2 text-sm">
                <p>There is still work open:</p>
                <ul className="list-disc pl-5">
                  {pending.counts.map((c) => <li key={c.name}>{c.count} {c.label}</li>)}
                </ul>
                <p>{SWITCH_OFF_CONSEQUENCE}</p>
              </div>
            ) : null}
          </Dialog>
        </SectionCard>
      )}
    </QueryState>
  );
}
